import { Elements } from "@stripe/react-stripe-js";
import { Appearance, loadStripe } from "@stripe/stripe-js";
import { STRIPE_KEY } from "@utils/constantsEnv";

const stripeTestPromise = loadStripe(STRIPE_KEY as string);

// Matches the underline look of the surrounding classic card elements
// (PaymentForm.tsx OPTIONS) so newer, appearance-API-driven elements like
// LinkAuthenticationElement don't render as a boxed, labeled input. The
// "flat" theme already draws its own underline — adding a second border
// here would double up with it, so only the color/spacing are overridden.
const appearance: Appearance = {
  theme: "flat",
  labels: "above",
  variables: {
    colorText: "black",
    colorTextPlaceholder: "rgba(57, 57, 57, 0.3)",
    fontSizeBase: "20px",
    borderRadius: "0px",
    focusBoxShadow: "none",
    focusOutline: "none",
    spacingUnit: "0px",
  },
  rules: {
    ".Block": {
      backgroundColor: "transparent",
      padding: "0",
    },
    ".Label": {
      fontWeight: "bold",
      fontSize: "12px",
      color: "black",
      textTransform: "uppercase",
      marginBottom: "6px",
    },
    ".Input": {
      backgroundColor: "transparent",
      padding: "5px 0",
      fontSize: "20px",
      color: "black",
      borderColor: "black",
    },
  },
};

// StripeContainer wraps both /checkout (a real charge) and /dashboard (saving
// a card with no purchase happening) — "setup" mode is correct for /dashboard,
// and is used here for /checkout too because this integration never confirms
// a PaymentIntent/SetupIntent through Elements; it only needs a mode so
// LinkAuthenticationElement can mount, then tokenizes via createPaymentMethod
// the same way the classic card fields already do. The real checkout amount
// is threaded separately into Apple Pay's own `paymentRequest` (useApplePay),
// which doesn't depend on this mode/amount at all.
// paymentMethodCreation: "manual" is required by Stripe whenever an
// ExpressCheckoutElement is mounted in the tree and the app calls
// createPaymentMethod({ elements }) itself instead of confirming a
// PaymentIntent — without it, that call throws an IntegrationError.
const StripeContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Elements
      stripe={stripeTestPromise}
      options={{ mode: "setup", currency: "usd", appearance, paymentMethodCreation: "manual" }}
    >
      {children}
    </Elements>
  );
};

export default StripeContainer;
